/**
 * Persists the player's personal best, character choice and sound setting locally. The future LeaderboardService
 * (server-validated, one entry per user) will complement – not replace – it.
 */
export interface ProgressStore {
  getBestScore(): number;
  /** Returns true if the score is a new personal best. */
  submitScore(score: number): boolean;
  /** Last chosen character id (unvalidated – may be outdated), null if none was stored. */
  getCharacter(): string | null;
  setCharacter(id: string): void;
  /** Background music switched on (default) or off by the player. */
  getMusicEnabled(): boolean;
  setMusicEnabled(enabled: boolean): void;
  /** Music and game sounds (default), game sounds only, or silence. */
  getSoundMode(): SoundMode;
  setSoundMode(mode: SoundMode): void;
}

/** The three steps of the sound switch (user 2026-10-05: "auch den Spielsound abschalten, nicht nur die Musik"). */
export enum SoundMode {
  All = 'ALL',
  EffectsOnly = 'EFFECTS_ONLY',
  Mute = 'MUTE',
}

const SOUND_CYCLE: readonly SoundMode[] = [SoundMode.All, SoundMode.EffectsOnly, SoundMode.Mute];

/** The next step of the sound switch: music and game sounds → game sounds only → silence → … */
export function nextSoundMode(mode: SoundMode): SoundMode {
  return SOUND_CYCLE[(SOUND_CYCLE.indexOf(mode) + 1) % SOUND_CYCLE.length]!;
}

/** The part of the Web Storage API the store needs (window.localStorage, or a stub in tests). */
export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

const KEY = 'bau-run.bestScore';
const CHARACTER_KEY = 'bau-run.character';
/**
 * 'on' / 'off' (music) or 'mute' (music and game sounds off) once the player toggled it – no entry means everything
 * on. Listed in the privacy policy (ructec.com/datenschutz#bauralley).
 */
const MUSIC_KEY = 'bau-run.music';
const STORED: Record<SoundMode, string> = { [SoundMode.All]: 'on', [SoundMode.EffectsOnly]: 'off', [SoundMode.Mute]: 'mute' };

/** localStorage if the browser allows it (private mode / blocked storage throws on access). */
function browserStorage(): KeyValueStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export class LocalProgressStore implements ProgressStore {
  private best = 0;
  private character: string | null;
  private sound: SoundMode;

  constructor(private readonly storage: KeyValueStorage | null = browserStorage()) {
    // Without storage everything is kept in memory for this session only.
    this.best = Number(this.read(KEY)) || 0;
    this.character = this.read(CHARACTER_KEY);
    const stored = this.read(MUSIC_KEY);
    this.sound = (Object.keys(STORED) as SoundMode[]).find((mode) => STORED[mode] === stored) ?? SoundMode.All;
  }

  getMusicEnabled(): boolean {
    return this.sound === SoundMode.All;
  }

  setMusicEnabled(enabled: boolean): void {
    this.setSoundMode(enabled ? SoundMode.All : SoundMode.EffectsOnly);
  }

  getSoundMode(): SoundMode {
    return this.sound;
  }

  setSoundMode(mode: SoundMode): void {
    this.sound = mode;
    this.write(MUSIC_KEY, STORED[mode]);
  }

  getBestScore(): number {
    return this.best;
  }

  submitScore(score: number): boolean {
    if (score <= this.best) return false;
    this.best = score;
    this.write(KEY, String(score)); // a failed write keeps the best score valid for this session
    return true;
  }

  getCharacter(): string | null {
    return this.character;
  }

  setCharacter(id: string): void {
    this.character = id;
    this.write(CHARACTER_KEY, id);
  }

  private read(key: string): string | null {
    try {
      return this.storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      this.storage?.setItem(key, value);
    } catch {
      // quota exceeded / blocked storage – the value stays valid in memory
    }
  }
}
